import React from 'react';
import { ModuleConfig } from '../../types/ModuleConfig';
import { INTEGRATIONS_TWO_STEP_MINUTES } from './utils/integrationsTwoStep';

// Command Centre components (lazy-loaded for performance)
const CommandDashboard = React.lazy(() => import('./CommandDashboard'));
const CeoDashboard = React.lazy(() => import('./CeoDashboard'));
const CommandDeals = React.lazy(() => import('./CommandDeals'));
// Same component, the other half of the pipeline — see the /command/leads route below.
// `createElement` rather than JSX because this is a .ts file, not .tsx.
const CommandLeads = React.lazy(async () => {
  const mod = await import('./CommandDeals');
  const Page = mod.default;
  return { default: () => React.createElement(Page, { view: 'leads' as const }) };
});
// Owner pages ALWAYS ask for two-step verification (Tom, 2026-09-29: "it should not let me see it
// unless 2FA"), whatever One Group's site switch says: no two-step → "Turn it on in Security";
// two-step on but not proven in the last 30 min → "Confirm it's you". The server's requireTwoFactor()
// on every Integrations operation is the security; this is the courtesy. The gate is loaded inside
// the lazy chunk so the site's main bundle doesn't carry it.
const withOwnerTwoStep = (load: () => Promise<{ default: React.ComponentType<any> }>) =>
  React.lazy(() =>
    Promise.all([load(), import('../authentication/components/RequireTwoFactor')]).then(([page, gate]) => ({
      // One confirmation lasts the whole sign-in (3 Oct, Tom) — no 30-minute re-ask; two-step still required.
      default: (props: any) => React.createElement(gate.RequireTwoFactor, { maxAgeMinutes: INTEGRATIONS_TWO_STEP_MINUTES } as any, React.createElement(page.default, props)),
    })),
  );
const CommandSettings = withOwnerTwoStep(() => import('./CommandSettings'));
const XeroCallback = withOwnerTwoStep(() => import('./XeroCallback'));
const MoneyLedger = React.lazy(() => import('./MoneyLedger'));
const CommissionOptions = React.lazy(() => import('./CommissionOptions'));
const CommissionBoard = React.lazy(() => import('./CommissionBoard'));
const CommissionTrends = React.lazy(() => import('./CommissionTrends'));
/**
 * The two retired commission URLs.
 *
 * They REDIRECT rather than render the board a second time. Mounting the same component on three
 * paths meant three URLs that all looked like different pages — someone lands on
 * /command/commissions, sees "Commission board", and has no way to know they're on the same page
 * a colleague calls /command/board. A redirect makes the address bar tell the truth, and old
 * links, bookmarks and the client's own notes still arrive somewhere correct.
 */
const RedirectToBoard = React.lazy(async () => {
  const { Navigate } = await import('react-router-dom');
  return { default: () => React.createElement(Navigate, { to: '/command/board', replace: true }) };
});
/**
 * The front door is the Dashboard for the Property division — the page Brian reads first
 * ("Where the commission sits" / "Who owes you the most"). `/command` redirects there so the
 * address bar tells the truth and old links still land somewhere useful. The integration status
 * page that used to sit at `/command` lives on at `/command/sources`.
 */
const RedirectToDashboard = React.lazy(async () => {
  const { Navigate } = await import('react-router-dom');
  return { default: () => React.createElement(Navigate, { to: '/command/dashboard', replace: true }) };
});
/** Old `/command/ceo?…` links (bookmarks, emails) land on the Dashboard with their unit and period kept. */
const RedirectCeoToDashboard = React.lazy(async () => {
  const { Navigate, useLocation } = await import('react-router-dom');
  const Redirect = () => {
    const { search } = useLocation();
    const next = new URLSearchParams(search);
    next.delete('view');
    const q = next.toString();
    return React.createElement(Navigate, { to: `/command/dashboard${q ? `?${q}` : ''}`, replace: true });
  };
  return { default: Redirect };
});
const CommissionDealDetail = React.lazy(() => import('./CommissionDealDetail'));
// New deal: admins only since 3 Oct (it was switched off 1 Oct — deals come from Pipedrive).
const NewCommissionDeal = React.lazy(() => import('./NewCommissionDeal'));
const CommandActivity = React.lazy(() => import('./CommandActivity'));
const CommandActivityEntryPage = React.lazy(() => import('./CommandActivityEntryPage'));
const CommandTags = React.lazy(() => import('./CommandTags'));
const CommissionImport = React.lazy(() => import('./CommissionImport'));
const PaymentDetail = React.lazy(() => import('./PaymentDetail'));
const NotificationSettings = React.lazy(() => import('./NotificationSettings'));

const commandModuleConfig: ModuleConfig = {
  // MODULE IDENTITY
  id: 'command',
  name: 'Command Centre',
  description: 'The One Group cockpit — GHL, Pipedrive & Xero in one place, with the commissions tracker',
  version: '0.14.0',
  enabled: true,
  category: 'business',
  order: 17,
  icon: '📡',
  color: '#5B21B6', // Purple

  // ROUTES — permissionLevel is resolved from the backend at runtime (SSOT)
  routes: [
    {
      path: '/command',
      component: RedirectToDashboard,
      permissionLevel: 'view',
      useLayout: false,
    },
    {
      // Where each figure comes from — GHL, Pipedrive and Xero connection status.
      path: '/command/sources',
      component: CommandDashboard,
      permissionLevel: 'view',
      useLayout: true,
    },
    {
      // Everything that happened across the module, newest first — the owner's feed.
      path: '/command/activity',
      component: CommandActivity,
      permissionLevel: 'admin',
      useLayout: true,
    },
    {
      // The Dashboard — one page (the CEO / CFO / Sales picker went 26 Oct 2026); business unit
      // and period live in the query string, Property by default.
      path: '/command/dashboard',
      component: CeoDashboard,
      permissionLevel: 'view',
      useLayout: true,
    },
    {
      // The Dashboard's old address — kept so links already sent still open it.
      path: '/command/ceo',
      component: RedirectCeoToDashboard,
      permissionLevel: 'view',
      useLayout: false,
    },
    {
      // LEADS and DEALS are two pages, not one.
      //
      // Sun on the 6 August call: "they're leads, not deals". A single page called Deals was
      // listing 17 rows from the Lead Pipeline — people who have shown interest, with no
      // commission attached. Calling those deals overstates the book to whoever reads the page,
      // and that is the sort of number that ends up quoted in a board meeting.
      path: '/command/leads',
      component: CommandLeads,
      permissionLevel: 'view',
      useLayout: true,
    },
    {
      path: '/command/deals',
      component: CommandDeals,
      permissionLevel: 'view',
      useLayout: true,
    },
    {
      // Every Xero transaction (in + out) with its match state.
      path: '/command/money',
      component: MoneyLedger,
      permissionLevel: 'view',
      useLayout: true,
    },
    {
      // THE COMMISSION BOARD — the record that replaced the spreadsheet.
      path: '/command/board',
      component: CommissionBoard,
      permissionLevel: 'view',
      useLayout: true,
      // The board owns its own width. The shared layout's Container caps a page at ~1200px, so
      // on a wide monitor the table was squeezed into a narrow column and scrolled sideways with
      // empty screen either side of it — the table wasn't too wide, the page was too narrow.
      layoutOptions: { noContainer: true },
    },
    {
      // Trends — money in, work won and outstanding, Brisbane period by period. Read-only.
      path: '/command/trends',
      component: CommissionTrends,
      permissionLevel: 'view',
      useLayout: true,
    },
    {
      // New deal — admins only (3 Oct, Tom: "command admin can create deals from this place, but no
      // one else … you won't even see the page"). Deals normally come from Pipedrive; this is for the
      // ones that don't (e.g. a private deal). Everyone else's links land on the board's explanation.
      path: '/command/board/new',
      component: NewCommissionDeal,
      permissionLevel: 'create',
      useLayout: true,
    },
    {
      // One deal, in full: details, instalments, matched payments and its whole history.
      path: '/command/board/:id',
      component: CommissionDealDetail,
      permissionLevel: 'view',
      useLayout: true,
    },
    {
      // ONE entry on a deal, on its own page — the link people are sent when they are tagged.
      path: '/command/board/:id/activity/:entryId',
      component: CommandActivityEntryPage,
      permissionLevel: 'view',
      useLayout: true,
    },
    {
      // Who is tagged on what and hasn't answered — every tag is a job (2026-10-01).
      path: '/command/tags',
      component: CommandTags,
      permissionLevel: 'view',
      useLayout: true,
    },
    {
      // Every event the module can tell somebody about, and who hears each one.
      path: '/command/notifications',
      component: NotificationSettings,
      permissionLevel: 'view',
      useLayout: true,
    },
    {
      // One Xero payment, addressable — so a payment matched to the wrong deal can be moved
      // from the deal it is sitting on, rather than hunted for again in the ledger.
      path: '/command/payment/:paymentId',
      component: PaymentDetail,
      permissionLevel: 'view',
      useLayout: true,
    },
    {
      // Bring a workbook in. Preview first, always — nothing is written until it is accepted.
      path: '/command/import',
      component: CommissionImport,
      permissionLevel: 'create',
      useLayout: true,
    },
    {
      // `/command/commissions` was Sun's mock-up with 70 INVENTED deals; `/command/projects` was
      // a read-only mirror of the Google Sheet. Both are gone — one board, one URL.
      path: '/command/commissions',
      component: RedirectToBoard,
      permissionLevel: 'view',
      useLayout: false,
    },
    {
      path: '/command/projects',
      component: RedirectToBoard,
      permissionLevel: 'view',
      useLayout: false,
    },
    {
      // The client's own dropdown lists behind the commission board (statuses, build stages,
      // people, builders) — edit-gated, because changing a list changes what everyone can pick.
      path: '/command/options',
      component: CommissionOptions,
      permissionLevel: 'admin',
      useLayout: true,
      // A table page takes the full page width (data-tables rule 7.0); the page sets its own padding.
      layoutOptions: { noContainer: true },
    },
    {
      // Integrations — Xero, GHL and the sheet. The module's `admin` level: COMMAND_ADMIN (3 Oct; COMMAND_INTEGRATIONS no longer exists)
      // or CLIENT_ADMIN, never plain ADMIN (owners only, 2026-09-28).
      path: '/command/settings',
      component: CommandSettings,
      permissionLevel: 'admin',
      useLayout: true,
    },
    {
      // Xero OAuth redirect target — must match the app's registered redirect URI exactly.
      // No layout: it's a transient hand-off page, not a destination.
      path: '/command/xero-callback',
      component: XeroCallback,
      permissionLevel: 'admin',
      useLayout: false,
    },
  ],

  // NAVIGATION
  navigation: [
    {
      label: 'Dashboard',
      path: '/command/dashboard',
      icon: '📊',
      permissionLevel: 'view',
    },
    {
      label: 'New deal',
      path: '/command/board/new',
      icon: '➕',
      permissionLevel: 'create',
    },
    {
      label: 'Deal Tracker',
      path: '/command/board',
      icon: '🏠',
      permissionLevel: 'view',
    },
    {
      label: 'Trends',
      path: '/command/trends',
      icon: '📈',
      permissionLevel: 'view',
    },
    {
      label: 'Money (Xero)',
      path: '/command/money',
      icon: '💵',
      permissionLevel: 'view',
    },
    {
      label: 'Activity',
      path: '/command/activity',
      icon: '🕓',
      permissionLevel: 'admin',
    },
    {
      label: 'Waiting on people',
      path: '/command/tags',
      icon: '📥',
      permissionLevel: 'view',
    },
    {
      label: 'Leads (GHL)',
      path: '/command/leads',
      icon: '🌱',
      permissionLevel: 'view',
    },
    {
      label: 'Deals (GHL)',
      path: '/command/deals',
      icon: '🔗',
      permissionLevel: 'view',
    },
    {
      label: 'Data sources',
      path: '/command/sources',
      icon: '📡',
      permissionLevel: 'view',
    },
    {
      label: 'Import Spreadsheet',
      path: '/command/import',
      icon: '📥',
      permissionLevel: 'create',
    },
    {
      label: 'Deal Tracker Settings',
      path: '/command/options',
      icon: '🎛️',
      permissionLevel: 'admin',
    },
    {
      label: 'Notifications',
      path: '/command/notifications',
      icon: '🔔',
      permissionLevel: 'view',
    },
    {
      label: 'Integrations',
      path: '/command/settings',
      icon: '⚙️',
      permissionLevel: 'admin',
    },
  ],

  // PERMISSIONS — EMPTY arrays; backend permissions.ts is the single source of truth
  permissions: {
    view: [],
    create: [],
    edit: [],
    delete: [],
  },

  // QUICK ACTIONS
  // ONE quick action, deliberately. A second entry pointing at /command/commissions made the
  // board look like two different features.
  quickActions: [
    {
      label: 'Dashboard',
      path: '/command/dashboard',
      icon: '📊',
      description: 'Where the commission sits and who owes you the most — the page to open first',
    },
    {
      label: 'Deal Tracker',
      path: '/command/board',
      icon: '🏠',
      description: 'Every deal, every commission, one record — with a full history of who changed what',
    },
  ],

  // CHANGELOG
  changelog: [
    {
      version: '0.14.0',
      releaseDate: '2026-10-26',
      title: 'One Dashboard',
      features: [
        '📊 The CEO, CFO and Sales dashboards showed the same figures, so they are now one page — just “Dashboard”. Old links still open it.',
        '🗓️ The period is picked with buttons — this financial year, this calendar year, this month, this week, and more — the same buttons as the Activity page.',
        '🏠 It opens on Property. Finance, Solar and All business units are one press away.',
      ],
    },
    {
      version: '0.13.0',
      releaseDate: '2026-10-03',
      title: 'The Commission Board is now the Deal Tracker',
      features: [
        '🏷️ Same page, new name — the Commission Board is now the Deal Tracker, and Board settings is Deal Tracker settings. Your links, bookmarks and saved home tiles still work.',
        '🔒 Private deals — an owner can make a deal private and choose who can see it. Everyone else doesn\'t see it anywhere.',
        '🤝 Tagging someone on a private deal asks first whether to share it with them.',
        '🔑 Three clear levels: view, edit (the team) and admin (the owners — Integrations, Activity, Deal Tracker settings, New deal).',
      ],
    },
    {
      version: '0.12.0',
      releaseDate: '2026-10-01',
      title: 'Every tag is a job',
      features: [
        '📥 Waiting on people — one page showing who has been tagged on which deals and hasn\'t answered yet, most waiting first, with a link to each one.',
        '✅ Mark done — fixed it on the deal instead of replying? Close the tag yourself; the deal\'s history records who did.',
        '👥 On the team — tick it for People and Brokers in Deal Tracker settings. Only the team is chased by email.',
        '📧 Monday email — each team member with something open gets their own list at 8am, with a link to each deal. Off until an owner switches it on in Integrations.',
      ],
    },
    {
      version: '0.11.0',
      releaseDate: '2026-09-28',
      title: 'The owners hold the keys',
      features: [
        '🔐 Integrations — the Xero, Go High Level and commission-sheet connections — is now for the owners only. Everyone else no longer sees the page or its link.',
        '👥 Only the owners can give a person access to the Command Centre from Board Settings → People. Anyone else sees that the person has no access yet, with no button.',
      ],
    },
    {
      version: '0.10.0',
      releaseDate: '2026-09-22',
      title: 'Which way is the business moving?',
      features: [
        '📈 Trends: money in, deals won, commission won and what is still outstanding — month by month or week by week, with each period compared to the one before it.',
        '🗓️ The Commission Board now has a date window: this month, last month, this financial year (from 1 July), last FY, this calendar year, the last 12 months, or any two dates you pick.',
        '🔍 Choose which date the window is about — money received, EOI signed, commission due, base, frame, practical completion or closing.',
        '🧾 Every total says the window it was worked out over, so a figure can never mean more than it should.',
        '🚧 The charts say what they could not include — deals with no signed date, payments with no date — instead of quietly leaving them out.',
      ],
    },
    {
      version: '0.9.0',
      releaseDate: '2026-09-17',
      title: 'New deal — start from the EOI — and every invoice in full',
      features: [
        '➕ New deal: drop the EOI, a contract or the builder\'s invoice — or take a photo of it — and the details fill in for you to check. Or just type them.',
        '🧾 Pick how the builder pays the commission — 50/50, thirds, all at slab and more — or set your own parts. The dollars work themselves out.',
        '✅ Every deal shows what is still to fill in — property, builder, strategist, broker, appointment setter, commission and dates — one tap away.',
        '📄 A payment now shows its invoice from Xero: every line description, the dates and the totals — and an Open in Xero button.',
        '🔢 Big numbers have their commas back, and Commission expected now counts open deals only.',
        '🏷️ Tagging someone in a reply works again.',
      ],
    },
    {
      version: '0.8.0',
      releaseDate: '2026-09-15',
      title: 'Tag people on a deal, tell them by email or text, and never lose a document',
      features: [
        '🏷️ On any deal\'s History tab, type @ to tag a person, a broker or a login — they get an email or, for something urgent, a text message with a link straight to that entry',
        '↩️ Anyone tagged replies from that link; the reply lands on the deal\'s record with their name and the time, and the tag shows told → replied',
        '📎 Drop a document anywhere on the deal page, or attach it in the note — it is on the record at once and is never edited or deleted',
        '📷 On a phone, Take a photo puts a site photo on the deal in one step',
        '🔗 Every entry on the History tab has its own page — the link people are sent',
      ],
    },
    {
      version: '0.7.0',
      releaseDate: '2026-09-15',
      title: 'Notifications, an activity feed, and a tidy-up of every page',
      features: [
        '📊 The CEO Dashboard for Property is now the front door — Where the commission sits and Who owes you the most are the first things you see',
        '🔔 Notifications: every event the Command Centre can fire is listed on one page; choose who hears about each one — the buyer, the broker, the strategist, finance, managers, or named people and logins',
        '👤 People and brokers on Board settings now carry an email, a mobile and a login, so a notification can actually reach them; adding a person creates their login under the hood',
        '🕓 Activity: everything that happened across the module on one feed, newest first — and each deal keeps its own',
        '🚫 Not deal money: wages, contractors, suppliers and the rent roll are set aside on the Money page instead of sitting in the unmatched pile',
        '📤 Outgoing Comms and the in-app Notifications inbox are installed on your site',
        '🔗 Quick links on the home page — your own shortcuts to the modules you use, in the order you want',
        '👓 Every page re-set for readability: larger text, bigger buttons, plain words, and a proper loading / error / empty state on each',
      ],
      notes: 'Tom is recording a short video walkthrough of these changes — the site will prompt you to watch it once it is ready.',
    },
    {
      version: '0.6.0',
      releaseDate: '2026-08-20',
      title: 'The Commission Board — your spreadsheet, replaced',
      features: [
        '🧹 Every piece of sample data is gone — the old demo commissions page and the sheet mirror now both open the real board, and the front page reads its figures from your actual deals',
        '↕️ Sort by any column, drag columns into the order you want, and choose which ones you see — all remembered on your device',
        '📄 Pages of 25 / 50 / 100 / 200, with the totals worked out across every matching deal rather than just the page in front of you',
        '🏠 Every deal from the workbook now lives on one page you can edit directly — no more tabs that disagree with each other',
        '📥 Import brings a spreadsheet in and shows you exactly what it contains BEFORE anything is saved; importing again updates deals instead of duplicating them',
        '🕓 Every change is recorded against the deal — who changed it, what it was before, and when',
        '🧮 Received and Still owed are worked out from the actual payments, so the three figures can never disagree the way they did on the sheet',
        '🔴 Base, frame and practical-completion dates turn red the day they pass',
        '🎛️ Board Settings — your own dropdown lists for statuses, build stages, people and builders; rename anything and every deal follows',
        '📊 One deal page with its instalments, matched payments, practical-completion checklist and full history',
        '📌 A movable Command Centre button — drag it wherever suits you',
      ],
      notes: 'Where the sheet said something we could not read, it is flagged rather than guessed at. The workbook is still yours; the Board is now where the answer lives.',
    },
    {
      version: '0.5.0',
      releaseDate: '2026-08-16',
      title: 'The money page + your commission sheet',
      features: [
        '💵 Money — every Xero payment in and out on one table',
        '🔍 Payments matched to commission deals automatically; anything uncertain is flagged for review rather than guessed',
        '🏠 Commission Deals — your commission sheet read straight into the Command Centre, refreshed on demand',
        '🔴 Due dates turn red once they pass — base, frame, practical completion and closing',
        '📋 A flow-chart of how a commission gets paid, so the page explains itself',
        '⚠️ Figures that don\'t add up, or cells we couldn\'t read, are surfaced instead of silently fixed',
      ],
      notes: 'The sheet stays your source of truth — this reads from it and never writes back. Your matched payments and notes here survive every refresh.',
    },
    {
      version: '0.3.0',
      releaseDate: '2026-08-07',
      title: 'CEO Dashboard — the executive view',
      features: [
        '📊 New CEO Dashboard with a Switch Dashboard picker — CEO, CFO and Sales views of the same live data',
        '🏢 Business-unit filter (Solar / Finance / Property), honest about which units are connected',
        '📅 What\'s owed and when — overdue plus 30 / 60 / 90 / 180-day due windows, live from Xero',
        '📈 12-month revenue trend from banked payments',
        '🔍 Every figure shows its source — tiles with no data feed say what they need instead of showing a zero',
      ],
      notes: 'Built from the dashboard mock-ups reviewed on the 6 August call. Profit and cash figures need one extra Xero approval.',
    },
    {
      version: '0.2.0',
      releaseDate: '2026-08-05',
      title: 'Xero connected — real money on the dashboard',
      features: [
        '🔗 Secure Xero connection (OAuth 2.0), read-only, revocable from either side',
        '💵 Revenue, invoices awaiting payment and recent payments now read live',
        '🧾 Money in (Xero) against each deal, matched by client name',
        '⚙️ Integrations page: masked credentials, clear-connection buttons, direct links into GHL',
      ],
      notes: 'Connected to One Property Australia Pty Ltd on 5 August.',
    },
    {
      version: '0.1.0',
      releaseDate: '2026-07-10',
      title: 'Command Centre — first pass',
      features: [
        '📡 Executive dashboard shell — GHL (live), Pipedrive & Xero panels',
        '💰 Commissions tracker — Act Now / Pipeline / All Projects / Reports (demo data from Sun\'s mock-up — retired 2026-08-20, replaced by the Commission Board)',
        '⚙️ Integrations page — GHL, Pipedrive & Xero API configuration',
        '⏳ Xero panel awaiting API access (OAuth connect)',
      ],
      notes: 'First pass: live Go High Level data, familiar commission demo data, Xero + Pipedrive pending access.',
    },
  ],

  // ROADMAP
  roadmap: [],
};

export default commandModuleConfig;
